import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IosShareIcon from '@mui/icons-material/IosShare';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    Alert,
    Box,
    Button,
    Paper,
    Stack,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useInstall } from '../hooks/useInstall';
import { showInstallPrompt } from '../lib/install';
import { colors } from '../themes/theme';

interface InstallGuideProps {
    /** Shown above the steps. Differs between the signup flow and the Me tab. */
    intro?: string;
}

export function InstallGuide({ intro }: InstallGuideProps) {
    const { platform, installed, canPrompt } = useInstall();
    const [prompting, setPrompting] = useState(false);

    if (installed) {
        return (
            <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />}>
                You&rsquo;re installed. Notifications can reach you here.
            </Alert>
        );
    }

    return (
        <Stack spacing={2}>
            {intro && <Typography color="text.secondary">{intro}</Typography>}

            <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2">
                    Its own icon, full screen, no browser bars — and it&rsquo;s the{' '}
                    <strong>only way a notification reaches you</strong> when a bet opens or
                    is about to close.
                </Typography>
            </Alert>

            {canPrompt && (
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<AddToHomeScreenIcon />}
                    disabled={prompting}
                    onClick={() => {
                        setPrompting(true);
                        void showInstallPrompt().finally(() => setPrompting(false));
                    }}
                >
                    {prompting ? 'Installing…' : 'Install Pony Up'}
                </Button>
            )}

            {platform === 'ios' && <IosSteps />}
            {platform === 'android' && <AndroidSteps showMenuRoute={!canPrompt} />}
            {platform === 'desktop' && <DesktopSteps />}
        </Stack>
    );
}

function StepShell({ children }: { children: React.ReactNode }) {
    return (
        <Paper sx={{ p: 2 }}>
            <Stepper orientation="vertical" activeStep={-1} sx={{ '& .MuiStepConnector-line': { minHeight: 12 } }}>
                {children}
            </Stepper>
        </Paper>
    );
}

const labelSx = { '& .MuiStepLabel-label': { fontWeight: 600, color: colors.ink } };

function IosSteps() {
    return (
        <>
            <Typography variant="overline" sx={{ color: colors.safety }}>
                iPhone &middot; Safari
            </Typography>
            <StepShell>
                <Step expanded>
                    <StepLabel sx={labelSx}>
                        Tap the Share button
                    </StepLabel>
                    <StepContent>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <IosShareIcon fontSize="small" sx={{ color: colors.safety }} />
                            <Typography variant="body2" color="text.secondary">
                                The square with an arrow, in the bar at the bottom of Safari.
                            </Typography>
                        </Stack>
                    </StepContent>
                </Step>
                <Step expanded>
                    <StepLabel sx={labelSx}>Scroll and tap &ldquo;Add to Home Screen&rdquo;</StepLabel>
                    <StepContent>
                        <Typography variant="body2" color="text.secondary">
                            It sits below the row of share icons. You may need to scroll the sheet.
                        </Typography>
                    </StepContent>
                </Step>
                <Step expanded>
                    <StepLabel sx={labelSx}>Tap Add</StepLabel>
                    <StepContent>
                        <Typography variant="body2" color="text.secondary">
                            The icon lands on your home screen. Open Pony Up from there from now on.
                        </Typography>
                    </StepContent>
                </Step>
            </StepShell>
            <Alert severity="warning">
                <Typography variant="body2">
                    It has to be <strong>Safari</strong> — Chrome and Firefox on iPhone
                    can&rsquo;t add apps to the home screen.
                </Typography>
            </Alert>
        </>
    );
}

function AndroidSteps({ showMenuRoute }: { showMenuRoute: boolean }) {
    return (
        <>
            <Typography variant="overline" sx={{ color: colors.safety }}>
                Android &middot; Chrome
            </Typography>
            {showMenuRoute ? (
                <StepShell>
                    <Step expanded>
                        <StepLabel sx={labelSx}>Open the browser menu</StepLabel>
                        <StepContent>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <MoreVertIcon fontSize="small" sx={{ color: colors.safety }} />
                                <Typography variant="body2" color="text.secondary">
                                    The three dots, top right.
                                </Typography>
                            </Stack>
                        </StepContent>
                    </Step>
                    <Step expanded>
                        <StepLabel sx={labelSx}>
                            Tap &ldquo;Install app&rdquo; or &ldquo;Add to Home screen&rdquo;
                        </StepLabel>
                        <StepContent>
                            <Typography variant="body2" color="text.secondary">
                                Chrome words it either way depending on the version.
                            </Typography>
                        </StepContent>
                    </Step>
                    <Step expanded>
                        <StepLabel sx={labelSx}>Confirm</StepLabel>
                        <StepContent>
                            <Typography variant="body2" color="text.secondary">
                                The icon lands in your app drawer and on your home screen.
                            </Typography>
                        </StepContent>
                    </Step>
                </StepShell>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Tap the button above and confirm. If you dismissed it, the same option is
                    under the browser menu as &ldquo;Install app&rdquo;.
                </Typography>
            )}
        </>
    );
}

function DesktopSteps() {
    return (
        <>
            <Typography variant="overline" sx={{ color: colors.safety }}>
                Desktop
            </Typography>
            <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Pony Up is built for a phone — that&rsquo;s where you&rsquo;ll be betting. On a
                    desktop browser you can still install it from the icon at the right-hand end of
                    the address bar.
                </Typography>
            </Paper>
            <Box>
                <Typography variant="body2" color="text.secondary">
                    To get it on your phone, open{' '}
                    <Box component="span" sx={{ color: colors.safety, fontWeight: 600 }}>
                        {window.location.host}
                    </Box>{' '}
                    there and follow the steps.
                </Typography>
            </Box>
        </>
    );
}
